import { Skeleton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import api from "../../../lib/api";

interface InventoryTurnoverProps {
  dateFilter: string;
  customRange?: { start: string; end: string };
  stateFilter?: string;
}

interface LocationData {
  state?: string;
  lga?: string;
  transactions: number;
  revenue: number;
  total_quantity: number;
}

export const InventoryTurnover = ({ dateFilter, customRange, stateFilter }: InventoryTurnoverProps) => {
  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState<LocationData[]>([]);

  useEffect(() => {
    fetchInventoryData();
  }, [dateFilter, customRange?.start, customRange?.end, stateFilter]);

  const getDateRange = (filter: string) => {
    if (filter === "custom" && customRange?.start && customRange?.end) {
        return { start_date: customRange.start, end_date: customRange.end };
    }

    const end = new Date();
    const start = new Date();

    switch (filter) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      case "180d":
        start.setDate(end.getDate() - 180);
        break;
      case "this-month":
        start.setDate(1);
        break;
      case "last-month":
        start.setMonth(end.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange(dateFilter);
      const params: any = { start_date, end_date, limit: 7 };
      
      // If state filter is selected, fetch LGA data instead of state data
      let endpoint = "/analytics/sales/by-state";
      if (stateFilter && stateFilter.trim() !== "") {
        endpoint = "/analytics/sales/by-lga";
        params.state = stateFilter;
      }

      const response = await api.get(endpoint, { params });
      
      // Handle both states and lgas response formats
      const data = response.data.data.states || response.data.data.lgas || [];
      setLocationData(data);
    } catch (error) {
      console.error("Failed to fetch inventory data:", error);
      setLocationData([]);
    } finally {
      setLoading(false);
    }
  };

  const maxValue = Math.max(...locationData.map(item => item.transactions), 1);

  return (
    <div className="bg-white p-4! rounded-2xl shadow">
      <div className="flex items-center justify-between mb-4!">
        <p className="text-sm! font-semibold!">
          {stateFilter && stateFilter.trim() !== "" 
            ? `Top LGAs in ${stateFilter} by Transaction Volume` 
            : "Top States by Transaction Volume"}
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} height="40px" width="100%" />
          ))}
        </div>
      ) : locationData.length === 0 ? (
        <div className="h-[280px] flex items-center justify-center text-gray-500 text-sm italic border border-dashed border-gray-200 rounded-lg">
          No data available for this period
        </div>
      ) : (
        <div className="space-y-3">
          {locationData.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{item.state || item.lga || "Unknown"}</span>
                  <span className="text-sm font-semibold text-gray-900">{item.transactions.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${(item.transactions / maxValue) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
